<template>
  <v-dialog v-model="show" max-width="600" persistent :content-class="isMobile ? 'box-selection-dialog--mobile' : ''">
    <v-card v-if="item">
      <v-card-title class="text-h6 mb-6 justify-center">
        {{ $t('subOrder.chooseBoxes') }}
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="6" class="py-0">
            <v-select
              v-model="newBoxType"
              :items="boxTypeItems"
              item-text="name"
              return-object
              :label="$t('subOrder.selectBoxModel')"
              :disabled="remainingToBox === 0"
              :style="remainingToBox === 0 ? 'opacity: 0.5' : ''"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="6" class="py-0">
            <v-btn
              :x-small="isMobile"
              :small="!isMobile"
              :style="isMobile ? 'height: auto; padding: 4px 6px; white-space: normal; word-break: break-word' : ''"
              text
              color="primary"
              :disabled="!newBoxType || remainingToBox === 0"
              @click="addEntry()"
            >
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('subOrder.addABox') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-divider class="mt-6 mb-6" />
        <v-row
          v-for="(entry, idx) in tempEntries"
          :key="idx"
          align="center"
          class="mb-1"
        >
          <v-col cols="6" class="py-0">
            <span class="body-2">{{ entryLabel(entry) }}</span>
          </v-col>
          <v-col cols="4" class="py-0">
            <v-text-field
              v-model.number="entry.quantity"
              type="number"
              min="1"
              :max="maxForEntry(entry)"
              :label="$t('product.qty')"
              dense
              outlined
              hide-details
              @change="clampQuantity(entry)"
            />
          </v-col>
          <v-col cols="2" class="text-right">
            <v-btn icon @click="removeEntry(idx)">
              <v-icon  color="red">mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-alert type="info" text dense :class="[tempEntries.length ? 'mt-3' : 'mt-0', isMobile ? 'caption' : '']">
          {{ $t('subOrder.remainingToBox') }}: <strong>{{ remainingToBox }}</strong>
        </v-alert>
      </v-card-text>
      <v-card-actions class="pa-0 mt-2">
        <v-btn
          color="primary"
          style="width: 50%"
          tile
          :disabled="!entriesValid"
          @click="save()"
        >
          {{ $t('settings.alerts.validate') }}
        </v-btn>
        <v-btn
          class="ml-0"
          color="secondary"
          style="width: 50%"
          tile
          @click="close()"
        >
          {{ $t('settings.alerts.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { formatBoxEntryLabel } from '@/helpers/helper'

  export default {
    name: 'BoxSelectionDialog',
    props: {
      value: {
        type: Boolean,
        required: true,
      },
      item: {
        type: Object,
        default: null,
      },
      boxTypeItems: {
        type: Array,
        required: true,
      },
      initialEntries: {
        type: Array,
        default: () => [],
      },
      isMobile: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        tempEntries: [],
        newBoxType: null,
      }
    },
    computed: {
      show: {
        get() {
          return this.value
        },
        set(val) {
          this.$emit('input', val)
        },
      },
      remainingToBox() {
        if (!this.item) return 0
        const total = this.item.validated_quantity || this.item.product_quantity
        const assigned = this.tempEntries.reduce((sum, e) => sum + (Number(e.quantity) || 0), 0)
        return Math.max(0, total - assigned)
      },
      entriesValid() {
        if (!this.item || this.tempEntries.length === 0) return false
        const total = this.item.validated_quantity || this.item.product_quantity
        let sum = 0
        for (const e of this.tempEntries) {
          const qty = Number(e.quantity)
          if (!Number.isInteger(qty) || qty <= 0) return false
          sum += qty
        }
        return sum === total
      },
    },
    watch: {
      value(val) {
        if (val) {
          this.tempEntries = JSON.parse(JSON.stringify(this.initialEntries))
          this.newBoxType = null
        } else {
          this.tempEntries = []
          this.newBoxType = null
        }
      },
    },
    methods: {
      addEntry() {
        if (!this.newBoxType) return
        const index = this.tempEntries.length + 1
        const remaining = this.remainingToBox
        this.tempEntries.push({
          boxType: { ...this.newBoxType },
          index,
          quantity: remaining > 0 ? remaining : 1,
        })
        this.newBoxType = null
      },
      removeEntry(idx) {
        this.tempEntries.splice(idx, 1)
        this.tempEntries.forEach((e, i) => {
          this.$set(e, 'index', i + 1)
        })
      },
      save() {
        if (!this.entriesValid) return
        this.$emit('save', [...this.tempEntries])
      },
      close() {
        this.$emit('close')
      },
      maxForEntry(entry) {
        const total = this.item.validated_quantity || this.item.product_quantity
        const otherSum = this.tempEntries
          .filter((e) => e !== entry)
          .reduce((sum, e) => sum + (Number(e.quantity) || 0), 0)
        return Math.max(1, total - otherSum)
      },
      clampQuantity(entry) {
        const max = this.maxForEntry(entry)
        const qty = Math.floor(Number(entry.quantity))
        if (!qty || qty < 1) {
          entry.quantity = 1
        } else if (qty > max) {
          entry.quantity = max
        } else {
          entry.quantity = qty
        }
      },
      entryLabel(entry) {
        return formatBoxEntryLabel(entry, this.$i18n.locale)
      },
    },
  }
</script>

<style scoped>
  .box-selection-dialog--mobile {
    margin: 8px !important;
  }
</style>
